import Head from "next/head"
import * as Sentry from "@sentry/node"
import { AppProps } from "next/app"
import { useRouter } from "next/router"
import {
  analytics,
  auth,
  DEV_ENVIRONMENTS,
  LanguageOption,
} from "@project/shared"
import { useEffect, useState } from "react"
import { signOut, onAuthStateChanged } from "firebase/auth"
import { message } from "antd"
import { CloseCircleFilled } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { QueryClient, QueryClientProvider } from "react-query"
import { GlobalStyles, AuthProvider } from "../utils"
import "../utils/css-imports"
import { logEvent, setCurrentScreen } from "firebase/analytics"

const queryClient = new QueryClient({ defaultOptions: {} })

if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
  Sentry.init({
    enabled: process.env.NODE_ENV !== "development",
    environment: `owner-${process.env.NODE_ENV}`,
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  })
}

const MyApp = ({ Component, pageProps }: AppProps) => {
  const routers = useRouter()
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  const [isOwner, setIsOwner] = useState(false)
  const { t } = useTranslation()

  const logOut = () => {
    signOut(auth)
    setUser(null)
    window.location.href = "/login"
  }

  useEffect(() => {
    if (process.env.NODE_ENV === "production") {
      const logAnalyticsEvent = (url: string) => {
        setCurrentScreen(analytics, url)
        logEvent(analytics, "screen_view", {
          firebase_screen: url,
          firebase_screen_class: "skeleton-owner",
        })
      }

      routers.events.on("routeChangeComplete", (url) => {
        logAnalyticsEvent(url)
      })

      logAnalyticsEvent(window.location.pathname)
      return () => {
        routers.events.off("routeChangeComplete", logAnalyticsEvent)
      }
    }
  }, [])

  const initialLoad = () => {
    onAuthStateChanged(auth, async (user) => {
      try {
        if (user !== null) {
          const idToken = await user!.getIdTokenResult()
          if (idToken.claims["operation_admin"]) {
            setUser(user)
            setIsOwner(true)
          } else {
            signOut(auth)
            setUser(null)
            message.error({
              key: "01",
              icon: <CloseCircleFilled onClick={() => message.destroy("01")} />,
              content: t("Unauthorized user"),
            })
          }
        }
        setLoading(false)
      } catch (error) {
        Sentry.captureException(error)
        message.error({
          key: "02",
          content: t("Something went wrong. Please contact administrator."),
          icon: <CloseCircleFilled onClick={() => message.destroy("02")} />,
        })
      }
    })
  }

  useEffect(() => {
    initialLoad()
  }, [])

  return (
    <>
      <Head>
        <title>{t("easyy Operation management login")}</title>
        <meta
          name={"viewport"}
          content={
            "width=device-width, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"
          }
        />
        <link rel={"preconnect"} href={"https://fonts.gstatic.com"} />
        <link
          href={
            "https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700&display=swap"
          }
          rel={"stylesheet"}
        />
      </Head>
      <GlobalStyles />
      <QueryClientProvider client={queryClient}>
        <AuthProvider
          loading={loading}
          user={user}
          isOwner={isOwner}
          setUser={setUser}
          logOut={logOut}
        >
          <Component {...pageProps} />
          {DEV_ENVIRONMENTS.includes(process.env.NEXT_PUBLIC_ENVIRONMENT) ? (
            <LanguageOption />
          ) : (
            <></>
          )}
        </AuthProvider>
      </QueryClientProvider>
    </>
  )
}

export default MyApp
